import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import Button from '@/components/shared/Button/Button';
import { useTourGuide } from './TourContext';
import { computeTooltipPlacement, getTargetRect, queryTourTarget, scrollTargetIntoView, type RectLike } from './tourUtils';

function formatStepCounter(stepIndex: number, total: number): string {
  const x = Math.min(stepIndex + 1, Math.max(total, 1));
  return `${x} / ${Math.max(total, 1)}`;
}

export function TourOverlay(props: {
  onRequestClose: () => void;
  onRequestFinish: () => void;
}) {
  const { onRequestClose, onRequestFinish } = props;
  const { status, steps, stepIndex, next, back, finish } = useTourGuide();

  const step = steps[stepIndex];
  const isRunning = status === 'running';
  const total = steps.length;

  const [targetEl, setTargetEl] = useState<HTMLElement | null>(null);
  const [targetRect, setTargetRect] = useState<RectLike | null>(null);
  const missingTargetRetriesRef = useRef(0);

  const tooltipRef = useRef<HTMLDivElement | null>(null);
  const [tooltipPos, setTooltipPos] = useState<{ top: number; left: number } | null>(null);

  const paddedRect = useMemo(() => {
    if (!targetRect) return null;
    const pad = 8;
    return {
      top: Math.max(targetRect.top - pad, 0),
      left: Math.max(targetRect.left - pad, 0),
      width: targetRect.width + pad * 2,
      height: targetRect.height + pad * 2,
      right: targetRect.right + pad,
      bottom: targetRect.bottom + pad,
    } satisfies RectLike;
  }, [targetRect]);

  // Resolve target element on step change
  useEffect(() => {
    if (!isRunning || !step) return;
    missingTargetRetriesRef.current = 0;

    const tryResolve = () => {
      const el = queryTourTarget(step.targetTourId);
      setTargetEl(el);
      if (el) {
        scrollTargetIntoView(el);
        return;
      }

      // Retry a few times to handle async rendering/data load.
      missingTargetRetriesRef.current += 1;
      if (missingTargetRetriesRef.current <= 12) {
        window.setTimeout(tryResolve, 250);
        return;
      }

      // Skip this step if target is still missing.
      if (stepIndex < steps.length - 1) next();
      else {
        finish();
        onRequestFinish();
      }
    };

    tryResolve();
  }, [isRunning, step, stepIndex, steps.length, next, finish, onRequestFinish]);

  // Track rect on scroll/resize while running
  useEffect(() => {
    if (!isRunning || !targetEl) return;
    let raf = 0;

    const update = () => {
      raf = window.requestAnimationFrame(() => {
        setTargetRect(getTargetRect(targetEl));
      });
    };

    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
      if (raf) window.cancelAnimationFrame(raf);
    };
  }, [isRunning, targetEl]);

  // Compute tooltip position after tooltip renders / rect updates
  useLayoutEffect(() => {
    if (!isRunning || !step || !paddedRect || !tooltipRef.current) {
      setTooltipPos(null);
      return;
    }
    const box = tooltipRef.current.getBoundingClientRect();
    const res = computeTooltipPlacement({
      targetRect: paddedRect,
      tooltipSize: { width: box.width, height: box.height },
      preferred: step.placement ?? 'auto',
      viewportPadding: 12,
      gap: 12,
    });
    setTooltipPos({ top: res.top, left: res.left });
  }, [isRunning, step, paddedRect]);

  useEffect(() => {
    if (!isRunning) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onRequestClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isRunning, onRequestClose]);

  if (!isRunning || !step) return null;

  const canBack = stepIndex > 0;
  const isLast = stepIndex === total - 1;

  const handleNext = () => {
    if (isLast) {
      finish();
      onRequestFinish();
    } else {
      next();
    }
  };

  const handleClose = () => {
    onRequestClose();
  };

  return (
    <div className="fixed inset-0 z-[999]">
      {/* Cut-out overlay: darken everything except target rect */}
      {paddedRect ? (
        <div
          className="fixed pointer-events-none"
          style={{
            top: `${paddedRect.top}px`,
            left: `${paddedRect.left}px`,
            width: `${paddedRect.width}px`,
            height: `${paddedRect.height}px`,
            borderRadius: 14,
            boxShadow: '0 0 0 9999px rgba(0,0,0,0.85)',
          }}
        />
      ) : (
        <div className="fixed inset-0 bg-black/85" />
      )}

      {/* Highlight box */}
      {paddedRect ? (
        <div
          className="fixed pointer-events-none"
          style={{
            top: `${paddedRect.top}px`,
            left: `${paddedRect.left}px`,
            width: `${paddedRect.width}px`,
            height: `${paddedRect.height}px`,
            borderRadius: 14,
            border: '2px solid rgba(34, 211, 238, 0.85)',
            boxShadow:
              '0 0 0 1px rgba(16, 185, 129, 0.25), 0 0 30px rgba(34, 211, 238, 0.35), 0 0 18px rgba(16, 185, 129, 0.25) inset',
          }}
        />
      ) : null}

      {/* Tooltip */}
      <div
        ref={tooltipRef}
        className="fixed w-[min(520px,calc(100vw-24px))] rounded-2xl border border-white/10 bg-black/80 backdrop-blur-md shadow-2xl"
        style={{
          top: tooltipPos ? `${tooltipPos.top}px` : '50%',
          left: tooltipPos ? `${tooltipPos.left}px` : '50%',
          transform: tooltipPos ? 'translate3d(0,0,0)' : 'translate(-50%,-50%)',
        }}
      >
        <div className="px-5 pt-5 pb-4">
          <div className="text-white font-extrabold text-lg">{step.title}</div>
          <div className="mt-2 text-white/75 text-sm leading-6">{step.description}</div>
        </div>
        <div className="flex items-center gap-3 px-5 pb-5">
          <Button variant="danger" className="bg-red-500 hover:bg-red-600" onClick={handleClose}>
            Close Guide
          </Button>
          <div className="flex-1 text-center text-white/60 text-sm font-semibold tabular-nums">
            {formatStepCounter(stepIndex, total)}
          </div>
          {canBack ? (
            <Button variant="secondary" onClick={back}>
              Back
            </Button>
          ) : null}
          <Button variant="success" onClick={handleNext}>
            {isLast ? 'Finish' : 'Next'}
          </Button>
        </div>
      </div>
    </div>
  );
}

